import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { Select } from '../../../components/ui';
import { getWidgetDefinition, EFORMS_FORM_WIDGET_TYPE } from '../widgetRegistry';
import { useEFormAppPages } from '../../e-forms/useData';

export interface WidgetConfigPanelProps {
  widgetId: string;
  widgetType: string;
  settings: Record<string, unknown>;
  onSave: (settings: Record<string, unknown>) => void;
  onClose: () => void;
}

const WidgetConfigPanel: React.FC<WidgetConfigPanelProps> = ({
  widgetId,
  widgetType,
  settings: initialSettings,
  onSave,
  onClose,
}) => {
  const { colors } = useTheme();
  const def = getWidgetDefinition(widgetType);
  const [settings, setSettings] = useState<Record<string, unknown>>(initialSettings ?? {});
  const { data: formAppPages = [] } = useEFormAppPages(
    undefined,
    widgetType === EFORMS_FORM_WIDGET_TYPE,
  );

  useEffect(() => {
    const merged = def ? { ...def.defaultSettings, ...initialSettings } : { ...initialSettings };
    setSettings(merged);
  }, [widgetId, widgetType, initialSettings, def]);

  const handleChange = (key: string, value: unknown) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(settings);
    onClose();
  };

  if (!def) {
    return (
      <div className="p-4">
        <p className={colors.textMuted}>Unknown widget type.</p>
        <button type="button" onClick={onClose} className={`mt-2 hover:underline ${colors.primaryFg}`}>
          Close
        </button>
      </div>
    );
  }

  const schema = def.settingsSchema as
    | Record<string, { type?: string; default?: unknown; min?: number; max?: number }>
    | undefined;
  const hasSchema = schema && Object.keys(schema).length > 0;

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4">
      <h3 className="text-lg font-semibold">{def.name} settings</h3>

      {widgetType === EFORMS_FORM_WIDGET_TYPE ? (
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Form Studio widget
          </label>
          <Select
            value={Number(settings.pageId) > 0 ? String(settings.pageId) : ''}
            onChange={(e) => {
              const pageId = Number(e.target.value);
              const page = formAppPages.find((item) => item.id === pageId);
              handleChange('pageId', pageId || 0);
              handleChange('pageName', page?.name ?? '');
            }}
            options={[
              { value: '', label: 'Select a widget…' },
              ...formAppPages.map((page) => ({
                value: String(page.id),
                label: page.definition?.name ? `${page.name} (${page.definition.name})` : page.name,
              })),
            ]}
            showClearButton={false}
          />
        </div>
      ) : hasSchema && schema ? (
        <div className="space-y-3">
          {Object.entries(schema).map(([key, field]) => {
            const value = settings[key] ?? field.default;
            if (field.type === 'number') {
              return (
                <div key={key}>
                  <label className="mb-1 block text-sm font-medium capitalize text-gray-700">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <input
                    type="number"
                    value={Number(value)}
                    min={field.min}
                    max={field.max}
                    onChange={(e) => handleChange(key, e.target.valueAsNumber)}
                    className={`w-full rounded border px-3 py-2 text-sm ${colors.border} ${colors.surface}`}
                  />
                </div>
              );
            }
            return (
              <div key={key}>
                <label className="mb-1 block text-sm font-medium capitalize text-gray-700">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </label>
                <input
                  type="text"
                  value={String(value ?? '')}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className={`w-full rounded border px-3 py-2 text-sm ${colors.border} ${colors.surface}`}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Limit</label>
            <input
              type="number"
              min={1}
              max={50}
              value={Number(settings.limit ?? 5)}
              onChange={(e) => handleChange('limit', e.target.valueAsNumber)}
              className={`w-full rounded border px-3 py-2 text-sm ${colors.border} ${colors.surface}`}
            />
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className={`rounded-lg border p-2 ${colors.border} ${colors.textMuted} ${colors.interactiveHover}`}
          title="Cancel"
          aria-label="Cancel"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <button
          type="submit"
          className={`rounded-lg p-2 ${colors.primary} ${colors.primaryText} hover:opacity-90`}
          title="Save"
          aria-label="Save"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </button>
      </div>
    </form>
  );
};

export default WidgetConfigPanel;
